import React, { useState, useEffect, useRef } from "react";
import { Modal, Box, Typography, Divider } from "@mui/material";
import Grid from "@mui/system/Grid";

import {
  ButtonVerifyStyle,
  HorizontalDivider,
  ModalBox,
  ModalGrid,
} from "../styles/styles";
import { ButtonContained } from "./button";
import { flexAllCentre } from "../../styles/mui/styles/display";
import { IDLE_TIMEOUT, SESSION_TIMEOUT } from "../../constants/User";

interface SessionTimeoutProps {
  logoutHppApp: () => void;
}

const SessionTimeout: React.FC<SessionTimeoutProps> = ({ logoutHppApp }) => {
  const [isIdle, setIsIdle] = useState(false);
  const [countdown, setCountdown] = useState(IDLE_TIMEOUT / 1000);

  const idleTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const isLoggingOut = useRef<boolean>(false);
  const lastActivityAtRef = useRef<number>(Date.now());
  const logoutDeadlineRef = useRef<number | null>(null);

  const clearCountdownInterval = () => {
    if (countdownInterval.current) {
      clearInterval(countdownInterval.current);
      countdownInterval.current = null;
    }
  };

  const triggerLogout = () => {
    if (isLoggingOut.current) {
      return;
    }

    if (idleTimeout.current) {
      clearTimeout(idleTimeout.current);
      idleTimeout.current = null;
    }
    clearCountdownInterval();
    setIsIdle(false);
    setCountdown(0);
    isLoggingOut.current = true;

    const performLogout = async () => {
      try {
        logoutHppApp();
      } catch (error) {
        isLoggingOut.current = false;
      }
    };

    performLogout();
  };

  const startIdleCountdown = (remainingMs: number) => {
    const safeRemainingMs = Math.max(remainingMs, 0);
    const remainingSeconds = Math.max(0, Math.ceil(safeRemainingMs / 1000));

    setIsIdle(true);
    setCountdown(remainingSeconds);
    logoutDeadlineRef.current = Date.now() + safeRemainingMs;

    clearCountdownInterval();
    countdownInterval.current = setInterval(() => {
      const deadline = logoutDeadlineRef.current;
      if (!deadline) {
        return;
      }

      const secondsLeft = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setCountdown(secondsLeft);

      if (secondsLeft <= 0) {
        triggerLogout();
      }
    }, 1000);
  };

  const resetIdleTimer = (delayMs: number = SESSION_TIMEOUT) => {
    if (idleTimeout.current) {
      clearTimeout(idleTimeout.current);
    }

    idleTimeout.current = setTimeout(() => {
      startIdleCountdown(IDLE_TIMEOUT);
    }, delayMs);
  };

  const evaluateInactivity = () => {
    if (isLoggingOut.current) {
      return;
    }

    const now = Date.now();
    const elapsed = now - lastActivityAtRef.current;
    const totalTimeout = SESSION_TIMEOUT + IDLE_TIMEOUT;

    if (elapsed >= totalTimeout) {
      triggerLogout();
      return;
    }

    if (elapsed >= SESSION_TIMEOUT) {
      const remainingMs = totalTimeout - elapsed;
      startIdleCountdown(remainingMs);
      return;
    }

    setIsIdle(false);
    setCountdown(IDLE_TIMEOUT / 1000);
    logoutDeadlineRef.current = null;
    clearCountdownInterval();
    resetIdleTimer(SESSION_TIMEOUT - elapsed);
  };

  const handleOk = () => {
    clearCountdownInterval();
    if (idleTimeout.current) {
      clearTimeout(idleTimeout.current);
      idleTimeout.current = null;
    }

    setIsIdle(false);
    setCountdown(IDLE_TIMEOUT / 1000);
    logoutDeadlineRef.current = null;
    isLoggingOut.current = false;
    lastActivityAtRef.current = Date.now();
    resetIdleTimer();
  };

  useEffect(() => {
    const handleActivity = () => {
      if (!isIdle) {
        lastActivityAtRef.current = Date.now();
        resetIdleTimer();
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        evaluateInactivity();
      }
    };

    const handleWindowFocus = () => {
      evaluateInactivity();
    };

    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keydown", handleActivity);
    window.addEventListener("scroll", handleActivity);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleWindowFocus);

    evaluateInactivity();

    return () => {
      if (idleTimeout.current) {
        clearTimeout(idleTimeout.current);
        idleTimeout.current = null;
      }
      clearCountdownInterval();
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("scroll", handleActivity);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [isIdle]);

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' + remainingSeconds : remainingSeconds}`;
  };

  return (
    <Modal
      open={isIdle}
      onClose={() => {}}
      aria-labelledby="session-timeout-modal"
      aria-describedby="session-timeout-modal-description"
      sx={flexAllCentre}
    >
        <Box sx={ModalBox}>
        <Grid container mb="0.4rem">
          <Grid size={{ xs: 11.5 }}>
            <Typography id="modal-modal-title" variant="h1">
              Your session will expire in {formatTime(countdown)}
            </Typography>
          </Grid>
        </Grid>
        <Divider sx={HorizontalDivider} />
        <Box pt={3} pb={3} sx={flexAllCentre}>
          <Typography id="modal-modal-title" variant="h1" component="h2">
            Hit Ok to stay logged in!
          </Typography>
        </Box>
        <Divider sx={HorizontalDivider} />
        <Grid container sx={ModalGrid}>
          <Grid size={{ xs: 12 }} sx={flexAllCentre}>
            <ButtonContained
              id="btnVerify"
              onClick={handleOk}
              label="Ok"
              style={ButtonVerifyStyle}
            />
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

export default SessionTimeout;